import type { LucideIcon } from "lucide-react";
import {
  BarChart3,
  Building2,
  CreditCard,
  FileText,
  Calendar,
  Gauge,
  Key,
  LifeBuoy,
  Mail,
  Plug,
  Search,
  Share2,
  ShoppingCart,
  Store,
  Users,
  Wallet,
  MessageCircle,
  UtensilsCrossed,
  Receipt,
  Landmark,
} from "lucide-react";
import type { SmRole } from "@/domain/sm-models";

export const SETTINGS_TAB_IDS = [
  "negocio",
  "tienda",
  "entrega",
  "salon",
  "pagos",
  "envios",
  "b2b",
  "politicas",
  "notificaciones",
  "mensajeria",
  "integraciones",
  "agenda",
  "impresoras",
  "config-financiera",
  "seo",
  "publicar",
  "tracking",
  "equipo",
  "uso",
  "suscripcion",
  "api",
  "soporte",
  "facturacion",
] as const;

export type SettingsTabId = (typeof SETTINGS_TAB_IDS)[number];

export type SettingsHubCluster =
  | "negocio"
  | "tienda"
  | "operacion"
  | "clientes"
  | "equipo"
  | "comunicacion"
  | "integraciones"
  | "cuenta";

export const SETTINGS_HUB_CLUSTER_LABELS: Record<SettingsHubCluster, string> = {
  negocio: "Negocio",
  tienda: "Tienda",
  operacion: "Operación",
  clientes: "Clientes",
  equipo: "Equipo",
  comunicacion: "Comunicación",
  integraciones: "Integraciones",
  cuenta: "Cuenta",
};

const TAB_ALIASES: Record<string, SettingsTabId> = {
  datos: "negocio",
  personalizacion: "tienda",
  marca: "tienda",
  policy: "politicas",
  correos: "notificaciones",
  email: "notificaciones",
  publish: "publicar",
  publicacion: "publicar",
  desarrolladores: "api",
  team: "equipo",
  printers: "impresoras",
  impresoras: "impresoras",
  logistica: "entrega",
  logistics: "entrega",
  calendar: "agenda",
  videollamada: "agenda",
};

export function normalizeSettingsTab(tab?: string | null): SettingsTabId | null {
  if (!tab) return null;
  const resolved = TAB_ALIASES[tab] ?? tab;
  return SETTINGS_TAB_IDS.includes(resolved as SettingsTabId) ? (resolved as SettingsTabId) : null;
}

export type SettingsSectionDef = {
  id: SettingsTabId;
  label: string;
  description: string;
  icon: LucideIcon;
  ownerOnly?: boolean;
  tourId?: string;
  /** Oculto del hub comercial — solo super_admin o VITE_FM_ADVANCED_SETTINGS=1 */
  advancedOnly?: boolean;
  productFlag?: string;
  /** Visible si CUALQUIERA de estos flags está on (OR). Si hay productFlag y productFlagsAny, también OR con productFlag. */
  productFlagsAny?: string[];
  /** Agrupa el hub: operación diaria vs configuración avanzada */
  hubGroup?: "daily" | "advanced";
  /** Agrupación comercial del hub de configuración. */
  hubCluster?: SettingsHubCluster;
  /** Visible por deep link, oculto del hub (vive en otro módulo o sub-hub). */
  hubHidden?: boolean;
  /** Perfiles de negocio para los que NO aplica esta sección (ej: envíos por correo no en gastronomía). */
  hiddenForProfiles?: string[];
  /** Solo visible para estos perfiles. */
  onlyProfiles?: string[];
};

/** Perfiles gastronómicos: usan delivery propio, no envío por correo. */

/** Perfiles gastronómicos — salón / mesas (incluye cafe, paridad con nav GASTRO). */
export const FOOD_PROFILES = ["fast_food", "restaurant", "cafe"];

export const SETTINGS_SECTIONS: SettingsSectionDef[] = [
  {
    id: "negocio",
    label: "Datos del negocio",
    description: "Nombre, contacto y redes",
    icon: Store,
    tourId: "settings-tab-datos",
    hubCluster: "negocio" },
  {
    id: "tienda",
    label: "Cómo compra el cliente",
    description: "Carrito, variantes, extras y catálogo",
    icon: ShoppingCart,
    hubCluster: "tienda" },
  {
    id: "salon",
    label: "Salón / Mesas",
    description: "Zonas del local y mesas",
    icon: UtensilsCrossed,
    onlyProfiles: [...FOOD_PROFILES],
    hubCluster: "tienda",
    hubHidden: true },
  {
    id: "politicas",
    label: "Políticas",
    description: "Cancelación y prepago",
    icon: FileText,
    hubCluster: "tienda" },
  {
    id: "notificaciones",
    label: "Comunicaciones",
    description: "Elegí cómo enviar tus mensajes y qué comunicaciones querés automatizar.",
    icon: Mail,
    hubCluster: "comunicacion" },
  {
    id: "pagos",
    label: "Pagos",
    description: "Cómo te pagan tus clientes",
    icon: Wallet,
    ownerOnly: true,
    tourId: "settings-tab-pagos",
    hubCluster: "operacion" },
  {
    id: "config-financiera",
    productFlag: "payment_config",
    label: "Cuentas y medios de cobro",
    description: "Cuentas bancarias, métodos de pago y dispositivos de cobro",
    icon: Landmark,
    ownerOnly: true,
    hubCluster: "operacion" },
  {
    id: "b2b",
    label: "Portal B2B",
    description: "Pedidos institucionales",
    icon: Building2,
    ownerOnly: true,
    hubCluster: "clientes",
    hubHidden: true },
  {
    id: "equipo",
    label: "Usuarios",
    description: "Quién entra al panel y qué puede hacer",
    icon: Users,
    ownerOnly: true,
    hubCluster: "equipo",
    hubHidden: true },
  {
    id: "publicar",
    label: "Publicación",
    description: "Link, QR y widget",
    icon: Share2,
    tourId: "settings-tab-publicar",
    hubCluster: "tienda" },
  {
    id: "mensajeria",
    productFlag: "whatsapp_notify",
    label: "WhatsApp",
    description: "Avisos de pedidos en tu WhatsApp",
    icon: MessageCircle,
    ownerOnly: true,
    hubCluster: "comunicacion" },
  {
    id: "integraciones",
    label: "Integraciones",
    description: "Qué está conectado a tu negocio",
    icon: Plug,
    tourId: "settings-tab-integraciones",
    hubCluster: "integraciones" },
  {
    id: "agenda",
    label: "Agenda y videollamada",
    description: "Google Calendar, Meet y Zoom por profesional",
    icon: Calendar,
    onlyProfiles: ["servicios"],
    hubCluster: "integraciones",
    hubHidden: true },
  {
    id: "facturacion",
    productFlagsAny: ["invoicing_tusfacturas", "invoicing_afip_direct"],
    label: "Facturación",
    description: "Comprobantes electrónicos",
    icon: Receipt,
    ownerOnly: true,
    hubCluster: "operacion" },
  {
    id: "seo",
    label: "SEO",
    description: "Cómo aparece tu tienda en Google",
    icon: Search,
    hubCluster: "tienda" },
  {
    id: "uso",
    label: "Uso",
    description: "Consumo, disponible y adicionales",
    icon: Gauge,
    ownerOnly: true,
    hubCluster: "cuenta" },
  {
    id: "suscripcion",
    label: "Suscripción",
    description: "Plan y facturación de Structura",
    icon: CreditCard,
    ownerOnly: true,
    hubCluster: "cuenta" },
  {
    id: "api",
    productFlag: "api_keys",
    label: "API",
    description: "Acceso técnico para desarrolladores",
    icon: Key,
    ownerOnly: true,
    hubGroup: "advanced",
    hubCluster: "integraciones" },
  {
    id: "soporte",
    productFlag: "tenant_support",
    label: "Soporte",
    description: "Tickets con Structura",
    icon: LifeBuoy,
    ownerOnly: true,
    hubCluster: "cuenta" },
  {
    id: "tracking",
    label: "Medición",
    description: "Analítica, publicidad y reseñas",
    icon: BarChart3,
    hubCluster: "integraciones",
    hubHidden: true },
];

export function settingsSectionsForRole(
  role?: SmRole | null,
  businessProfile?: string | null,
  opts?: { showAdvanced?: boolean; productFlags?: Record<string, boolean> | null },
) {
  const showAdvanced =
    opts?.showAdvanced ||
    role === "super_admin" ||
    import.meta.env.VITE_FM_ADVANCED_SETTINGS === "1";
  return SETTINGS_SECTIONS.filter((s) => {
    if (s.advancedOnly && !showAdvanced) return false;
    if (opts?.productFlags) {
      const anyKeys = [
        ...(s.productFlag ? [s.productFlag] : []),
        ...(s.productFlagsAny ?? []),
      ];
      if (anyKeys.length > 0 && !anyKeys.some((k) => opts.productFlags?.[k] === true)) return false;
    }
    if (s.hiddenForProfiles && businessProfile && s.hiddenForProfiles.includes(businessProfile)) return false;
    if (s.onlyProfiles && businessProfile && !s.onlyProfiles.includes(businessProfile)) return false;
    if (!s.ownerOnly) return true;
    return role === "owner" || role === "super_admin";
  });
}

export function settingsHubSections(
  role?: SmRole | null,
  businessProfile?: string | null,
  opts?: { showAdvanced?: boolean; productFlags?: Record<string, boolean> | null },
) {
  return settingsSectionsForRole(role, businessProfile, opts).filter((s) => !s.hubHidden);
}

export function settingsSectionById(id: SettingsTabId) {
  return SETTINGS_SECTIONS.find((s) => s.id === id);
}
